import {afterRender, ChangeDetectionStrategy, Component, ElementRef, signal, viewChild} from '@angular/core'
import {IndustryRegionPageResolved} from './industry-region-page.resolver'
import {mergeMap, Observable, of, zip} from 'rxjs'
import {OptableLayout} from '../../../../core/components/layout/optable-layout.interface'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {Breadcrumb} from '../../../../craft/breadcrumbs/breadcrumbs.component'
import {ActivatedRoute} from '@angular/router'
import {map} from 'rxjs/operators'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {LinkService, OTHER_OFFERING_ID} from '../../../../core/i18n/link.service'
import {TranslocoService} from '@ngneat/transloco'
import {CallToAction} from '../../../../craft/button/call-to-action/call-to-action.component'
import {RegionalDirectoryCssIdPipe} from '../craftsmen-region-directory-css-id.pipe'
import {ReplacePropsPipe} from '../../../../shared/pipes/replace-props.pipe'
import {isRelatedArticlesSection} from '../../../../core/content/types/craftsmen-region-types'
import {IndustryRegionPage_GetOfferingsGql} from '../../../../../__generated__/types'
import {FILTER_BY_REGION_QUERY_PARAM} from '../../offering-page/offering-page.component'

@Component({
  selector: 'bgo-industry-region-page',
  templateUrl: './industry-region-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IndustryRegionPageComponent extends PageComponent implements OptableLayout {
  readonly optOutOfLayout = true
  readonly transparentNavbar = false
  readonly page$: Observable<
    IndustryRegionPageResolved & {breadcrumbs: Breadcrumb[]; primaryCta: CallToAction; secondaryCta: CallToAction} & {
      offerings: {
        id: string
        slug?: string
        label: string
        clientFacingLabel?: string
        link: string
        queryParams: {[FILTER_BY_REGION_QUERY_PARAM]: string}
      }[]
    }
  >
  protected showStickyFooter = true

  hasScrolled = signal(false)
  contentOverflow = signal(false)
  offeringsContainer = viewChild<ElementRef<HTMLDivElement>>('offeringsContainer')

  constructor(
    route: ActivatedRoute,
    getOfferingsGql: IndustryRegionPage_GetOfferingsGql,
    private readonly translocoService: TranslocoService,
    private readonly linkService: LinkService,
  ) {
    super()
    this.page$ = route.data.pipe(
      map(data => data['content']),
      mergeMap((page: IndustryRegionPageResolved) => {
        return getOfferingsGql.fetch({industryId: page.industry.id.toString()}).pipe(
          map(({data: {industriesByIds}}) => {
            return {
              page,
              offerings: industriesByIds.length
                ? industriesByIds[0].offerings.map(o => ({
                    ...o,
                    link: linkService.localizedPathTo(PUBLIC_ROUTES.OFFERING_PAGE, {
                      routeParams: {pageSlug: o.slug ?? o.id},
                    }),
                    queryParams: {
                      [FILTER_BY_REGION_QUERY_PARAM]: page.region.slug,
                    },
                  }))
                : [],
            }
          }),
        )
      }),
      mergeMap(page =>
        zip([
          of(page),
          this.translocoService.selectTranslate('craftsmenRegionDirectoryPage.breadcrumbs.regionsDirectoryPage'),
        ]),
      ),
      map(([{page, offerings}, craftsmenPageBreadcrumbName]) => {
        const breadcrumbs: Breadcrumb[] = [
          {
            name: craftsmenPageBreadcrumbName,
            link: linkService.localizedPathTo(PUBLIC_ROUTES.CRAFTSMEN_REGION_PAGE_ROOT),
          },
          {
            name: page.region.regionName,
            link: linkService.localizedPathTo(PUBLIC_ROUTES.CRAFTSMEN_REGION_PAGE, {
              routeParams: {regionSlug: page.region.slug},
            }),
          },
          {
            name: page.industry.label,
          },
        ]

        const primaryCta: CallToAction = {
          type: 'Path',
          label: this.translocoService.translate('craftsmenRegionDirectoryPage.ctas.primary'),
          value: PUBLIC_ROUTES.JOB_REQUEST,
          routeParams: {
            offeringId: OTHER_OFFERING_ID,
          },
        }

        const secondaryCta: CallToAction = {
          type: 'Path',
          label: this.translocoService.translate('craftsmenRegionDirectoryPage.ctas.secondary'),
          value: PUBLIC_ROUTES.CRAFTSMEN_REGION_INDUSTRY_PAGE,
          routeParams: {
            regionSlug: page.region.slug,
            industrySlug: page.industry.slug,
          },
          fragment: new RegionalDirectoryCssIdPipe().transform(0),
        }

        return {...page, breadcrumbs, primaryCta, secondaryCta, offerings}
      }),
    )

    afterRender(() => {
      const container = this.offeringsContainer()
      if (container?.nativeElement) {
        const containerEl = container.nativeElement
        const padding = 10
        this.contentOverflow.set(containerEl.scrollHeight > containerEl.clientHeight + padding)
      }
    })
  }

  navigateToAboutUsPage(): Promise<boolean> {
    return this.linkService.navigateTo(PUBLIC_ROUTES.ABOUT_US)
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.page$.pipe(
      map(page => ({
        title: page.seo?.title
          ? new ReplacePropsPipe().transform(page.seo.title, {
              regionName: page.region.regionName,
              industryName: page.industry.label,
              industryTextVariable: page.industry.textVariable || page.industry.label,
            })
          : new ReplacePropsPipe().transform(page.cover.title, {
              regionName: page.region.regionName,
              industryName: page.industry.label,
              industryTextVariable: page.industry.textVariable || page.industry.label,
            }),
        description: page.seo?.description
          ? new ReplacePropsPipe().transform(page.seo.description, {
              regionName: page.region.regionName,
              industryName: page.industry.label,
              industryTextVariable: page.industry.textVariable || page.industry.label,
            })
          : new ReplacePropsPipe().transform(page.cover.subtitle, {
              regionName: page.region.regionName,
              industryName: page.industry.label,
              industryTextVariable: page.industry.textVariable || page.industry.label,
            }),
        // Region is only able to override if true.
        doNotIndexPage: page.region.doNotIndex || (page.seo?.doNotIndex ?? false),
        canonicalUrl: page.seo?.canonicalUrl,
      })),
    )
  }

  onScroll(e: Event) {
    const container = e.target as HTMLElement
    this.hasScrolled.set(container.scrollTop > 0)
  }

  protected readonly isRelatedArticlesSection = isRelatedArticlesSection
}
